import * as $ from 'jquery';
import * as _ from 'underscore';

import { IGlob } from './../Glob';
import BaseStore from '../../../../_common/platform/ts/BaseStore';
import { Constants } from '../../../../_common/platform/ts/Constants';
import { apiCall } from '../../../../_common/platform/ts/AqlUtility';
import { config } from '../../ts/config';
import serializeWorkspace from '../../../../_common/platform/ts/serializeWorkspace';

import { AppDispatcher as Dispatcher } from '../dispatcher/AppDispatcher';
import { AppActions, I, check as checkAction } from '../actions/AppActions';
import { IValuesCollection } from '../../../../_common/platform/ts/appTheme';
import * as Utilities from '../../../../_common/platform/ts/Utilities';

export interface IWorkspaceEditState {
    id: number;
    name?: string;
    data?: {
        widgets?: any[];
        theme?: IValuesCollection;
        settings?: any;

        layout?: {
            pos: any[];
            size: any[];
            columns: any[];
            rows: any[];
        };
    };

    loading: boolean;
    actionLoading: boolean;
    showDirSelect: boolean;
}

export class WorkspaceEditStore extends BaseStore<IWorkspaceEditState, AppActions, Dispatcher, IGlob> {
    public static getInitialState() {
        return {
            id: null,
            loading: false,
            actionLoading: false,
            showDirSelect: false
        };
    }

    public _state: IWorkspaceEditState = WorkspaceEditStore.getInitialState();
    public static _name = 'workspace-edit';

    private _defs: {
        initialLoad?: JQueryDeferred<any>;
        save?: JQueryDeferred<any>;
        createSave?: JQueryDeferred<any>;
    } = {};

    private workspaceShow(id: number, name: string, data) {
        this._state = WorkspaceEditStore.getInitialState();
        this._state.id = id;
        this._state.name = name;
        this._state.data = data;

        if (id != null && this._state.name == null && this._state.data == null) {
            this.actions.workspaceLoadContent(id);
        }
    }

    private workspaceHide() {
        this._state = WorkspaceEditStore.getInitialState();
    }

    private requestName(id: number) {
        return apiCall(this.actions, 'workspace_GetName', {
            wsid: id
        });
    }

    private requestData(id: number) {
        return apiCall(this.actions, 'workspace_GetData', {
            wsid: id
        });
    }

    private workspaceLoadContent(id: number) {
        this._state.loading = true;

        if (this._defs.initialLoad != null && this._defs.initialLoad.state() === 'pending') {
            this._defs.initialLoad.resolve({ result: '' }, { result: '{}' });
        }

        const def = $.Deferred();
        const defs = [this.requestName(id), this.requestData(id)];

        $.when
            .apply($, defs)
            .done((nameResp, dataResp) => {
                def.resolve(nameResp, dataResp);
            })
            .fail((obj) => {
                def.reject(obj);
            });

        this._defs.initialLoad = def;

        def.done((nameResp, dataResp) => {
            this._state.name = nameResp.result;
            this._state.data = JSON.parse(dataResp.result);
        });

        def.fail(() => {
            this.glob.mainRouter.navigate('/', {
                trigger: true,
                replace: true
            });
        });

        def.always(() => {
            this.actions.workspaceLoadContentFinish(id);
        });
    }

    private workspaceLoadContentFinish() {
        this._state.loading = false;
    }

    private static getEmptyTableUnit() {
        return {
            ratio: 1
        };
    }

    private create() {
        this._state.data = {
            widgets: [],

            layout: {
                pos: [],
                size: [],
                columns: [WorkspaceEditStore.getEmptyTableUnit()],

                rows: [WorkspaceEditStore.getEmptyTableUnit()]
            }
        };

        this._state.name = 'New workspace';
        this._state.id = null;
    }

    private workspaceSave(id: number, data: any, dateLastUpdated, def: JQueryDeferred<any>) {
        this._state.actionLoading = true;

        if (this._defs.save != null && this._defs.save.state() === 'pending') {
            this._defs.save.resolve(null);
        }

        let dataToSave: string;

        if (typeof data === 'string') {
            dataToSave = data;
        } else {
            dataToSave = serializeWorkspace(data, true);
        }

        if (def == null) {
            def = $.Deferred<any>();
        }

        this._defs.save = def;

        apiCall(
            this.actions,
            'workspace_Update',
            {
                wsid: id,
                data: dataToSave,
                dateLastUpdated: dateLastUpdated
            },
            null,
            def
        );

        def.done((updResp) => {
            const { updatedBy, dateUpdated } = updResp.result || {};

            if (updatedBy != null) {
                const userDef = $.Deferred<any>();

                apiCall(this.actions, 'user_GetAccountInfo', { userid: updatedBy }, null, userDef);

                userDef.always((udResp) => {
                    const { displayName } = udResp.result || {};
                    const dateUpdatedLocal = new Date(dateUpdated);

                    this.glob.DialogActions.createDialog(
                        'Workspace has been updated',
                        `This workspace has been updated ${
                            displayName != null ? ' by ' + displayName : ''
                        } at ${dateUpdatedLocal.toLocaleString()}. ` +
                            'Click Save As to save it with another name or Reload to get the latest changes from server.',
                        () => {
                            this.actions.workspaceSaveAs(id, data, false);
                        },
                        () => {
                            this.actions.workspaceViewLoadContent(id, true, null, true);
                            this.actions.workspaceReset(id);
                        },
                        () => {},
                        'confirm',
                        {
                            confirmTitle: 'Save As',
                            cancelTitle: 'Reload'
                        }
                    );
                });

                this.actions.workspaceSaveFinish(false);
            } else {
                this.actions.workspaceSaveFinish(true, id, null, null, dataToSave, updResp.result.dateUpdated);
                this.actions.showNotification_Success('Workspace "' + id + '" is saved');
            }
        });

        def.fail(() => {
            this.actions.workspaceSaveFinish(false);
        });
    }

    private workspaceSaveAs(id: number, data: any, close: boolean, def: JQueryDeferred<any>) {
        let _overwrite = false;
        let _dir = 1;

        const instance = this.glob.WorkspaceStore.getInstanceState(id);
        const { wsPathDirId } = instance;

        if (wsPathDirId != null && wsPathDirId.length > 0) {
            _dir = wsPathDirId[wsPathDirId.length - 1];
        }

        let newName = instance.name;

        if (id != null) {
            newName += ' (copy)';
        }

        // if (this.state.overwrite == null) {
        //     _overwrite = false;
        // } else {
        //     _overwrite = this.state.overwrite;
        // }

        this.actions.workspaceHideSidebar();

        this.glob.DialogActions.createDialog(
            'Select a folder and name for workspace',
            '',
            (dirid, name, overwrite) => {
                let parsedData: any;

                if (typeof data === 'string') {
                    parsedData = JSON.parse(data);
                } else {
                    parsedData = data;
                }

                Utilities.renewIDsAndCIDs(parsedData);

                const dataToSave = serializeWorkspace(parsedData, true);
                const directoryId = dirid != null ? parseInt(dirid) : null;

                this.actions.workspaceCreateSave(directoryId, name, overwrite, dataToSave, id, close);

                if (def != null && typeof def.resolve === 'function') {
                    def.resolve();
                }
            },
            () => {
                if (def != null && typeof def.reject === 'function') {
                    def.reject();
                }
            },
            () => {
                if (def != null && typeof def.reject === 'function') {
                    def.reject();
                }
            },
            'workspaceDirSelect',
            {
                selectTitle: 'Save',
                rootId: config.WIDGETS_ROOT_DIR_ID,
                nameSelect: true,
                name: newName,
                overwrite: _overwrite,
                overwriteShow: true,
                wsPathDirId: wsPathDirId, //VR: pass it to know path of this WS
                dir: _dir, // see the interface AppActions => "ConfigWorkspaceDirSelect"
                allowSelectItem: true,
                allowOpenWorkspace: false,
                hiddenScroll: true
            }
        );
    }

    private workspaceSaveFinish() {
        this._state.actionLoading = false;
    }

    private showDirSelect() {
        this._state.showDirSelect = true;
        this.actions.dirsContentLoad();
    }

    private hideDirSelect() {
        this._state.showDirSelect = false;
    }

    private createSave(dirId, name: string, overwrite: boolean, data: string, origin?, close = false, state?) {
        this._state.actionLoading = true;

        if (this._defs.createSave != null && this._defs.createSave.state() === 'pending') {
            this._defs.createSave.reject(null);
        }

        const def = $.Deferred<any>();

        this._defs.createSave = def;

        apiCall(
            this.actions,
            'workspace_Create',
            {
                dirid: dirId,
                name: name,
                overwrite: overwrite,
                data: data
            },
            null,
            def
        );

        def.done((resp) => {
            const id = resp.result;

            if (close !== true) {
                this.glob.mainRouter.navigate('/tabs/' + this.glob.AppStore._state.tabDisplay + '/workspaces/' + id, {
                    trigger: true,
                    replace: false
                });
            }

            this.actions.showNotification_Success(
                'Workspace "' + name + '" was successfully created in the folder "' + dirId + '"'
            );
            this.actions.workspaceSaveFinish(
                true,
                id,
                dirId,
                name,
                data,
                new Date().toISOString(),
                false,
                origin,
                state
            );
        });

        def.fail(() => {
            this.actions.workspaceSaveFinish(false);
        });
    }

    private remove(id: number, instanceId: string, defExternal: JQueryDeferred<any>) {
        const def = apiCall(this.actions, 'workspace_Delete', {
            wsid: id
        });

        def.done(() => {
            this.actions.workspaceRemoveFinish(true, id, instanceId);
            this.actions.showNotification_Success('Workspace "' + id + '" was successfully deleted');

            if (defExternal != null) {
                defExternal.resolve();
            }
        });

        def.fail(() => {
            this.actions.workspaceRemoveFinish(false, id, instanceId);

            if (defExternal != null) {
                defExternal.reject();
            }
        });
    }

    private workspaceSetTheme(id: number, value: IValuesCollection) {
        if (this._state.id !== id) {
            return;
        }

        const instance = this._state;

        if (instance == null || instance.data.theme == null) {
            return;
        }

        const workspaceInstance = this.glob.WorkspaceStore.getInstanceState(id);

        workspaceInstance.edititngStarted = true;
        instance.data.theme = value;
        instance.data = _.clone(instance.data);
    }

    protected eventsHandler(payload) {
        if (!super.eventsHandler(payload)) {
            return false;
        }

        const a = payload.action;
        const _case = checkAction;

        do {
            if (_case<I.WORKSPACE_SHOW>(a, Constants.WORKSPACE_SHOW)) {
                this.workspaceShow(a.id, a.name, a.data);
                this.emitChange();
                break;
            }

            if (_case<I.WORKSPACE_HIDE>(a, Constants.WORKSPACE_HIDE)) {
                this.workspaceHide();
                this.emitChange();
                break;
            }

            if (_case<I.WORKSPACE_CONTENT_LOAD>(a, Constants.WORKSPACE_CONTENT_LOAD)) {
                this.workspaceLoadContent(a.id);
                this.emitChange();
                break;
            }

            if (_case<I.WORKSPACE_CONTENT_LOAD_FINISH>(a, Constants.WORKSPACE_CONTENT_LOAD_FINISH)) {
                this.workspaceLoadContentFinish();
                this.emitChange();
                break;
            }

            if (_case<I.WORKSPACE_SAVE>(a, Constants.WORKSPACE_SAVE)) {
                this.workspaceSave(a.id, a.data, a.dateLastUpdated, a.def);
                this.emitChange();
                break;
            }

            if (_case<I.WORKSPACE_SAVE_AS>(a, Constants.WORKSPACE_SAVE_AS)) {
                this.workspaceSaveAs(a.id, a.data, a.close, a.def);
                this.emitChange();
                break;
            }

            if (_case<I.WORKSPACE_SAVE_FINISH>(a, Constants.WORKSPACE_SAVE_FINISH)) {
                this.workspaceSaveFinish();
                this.emitChange();
                break;
            }

            if (_case<I.WORKSPACE_CREATE_NEW>(a, Constants.WORKSPACE_CREATE_NEW)) {
                this.create();
                this.emitChange();
                break;
            }

            if (_case<I.WORKSPACE_SHOW_DIR_SELECT>(a, Constants.WORKSPACE_SHOW_DIR_SELECT)) {
                this.showDirSelect();
                this.emitChange();
                break;
            }

            if (_case<I.WORKSPACE_HIDE_DIR_SELECT>(a, Constants.WORKSPACE_HIDE_DIR_SELECT)) {
                this.hideDirSelect();
                this.emitChange();
                break;
            }

            if (_case<I.WORKSPACE_CREATE_SAVE>(a, Constants.WORKSPACE_CREATE_SAVE)) {
                this.createSave(a.dirId, a.name, a.overwrite, a.data, a.origin, a.close, a.state);
                this.emitChange();
                break;
            }

            if (_case<I.WORKSPACE_DELETE>(a, Constants.WORKSPACE_DELETE)) {
                this.remove(a.id, a.instanceId, a.def);
                this.emitChange();
                break;
            }

            if (_case<I.WORKSPACE_SET_THEME>(a, Constants.WORKSPACE_SET_THEME)) {
                this.workspaceSetTheme(a.id, a.value);
                this.emitChange();
                break;
            }
        } while (false);

        return true;
    }

    constructor(glob: IGlob, dispatcher: Dispatcher, actions: AppActions) {
        super(glob, dispatcher, actions, WorkspaceEditStore._name);
    }

    static get(glob: IGlob, dispatcher: Dispatcher, actions: AppActions) {
        return new WorkspaceEditStore(glob, dispatcher, actions);
    }
}
